import { toRaw, isProxy } from 'vue'
import { type Table } from 'dexie'
import { db, SETTINGS_KEY } from './schema'
import type { Book, Bookmark, Highlight, HighlightComment, ReadingSession, Shelf, Summary, Translation } from '@/types/domain'
import type { BookIndexChunk } from '@/types/search'
import type { EmbeddingRow } from '@/types/embeddings'
import type { AudioChunkRow } from '@/types/audio'
import { DEFAULT_SETTINGS, type Settings } from '@/types/settings'
import { isQuotaError, StorageQuotaError } from './storage-quota'

/**
 * Capa de acceso a datos. Aísla el resto de la app de Dexie/IndexedDB
 * para que los stores de Pinia trabajen con funciones claras y podamos
 * cambiar el backend (o añadir sincronización) sin tocar la UI.
 *
 * Todas las funciones lanzan si algo falla; los stores deciden cómo
 * informar al usuario (toasts, estados de error, etc.).
 */

/**
 * Desreactiva un objeto (quita proxies de Vue/Pinia) de forma profunda
 * para que IndexedDB pueda clonarlo. Los Blobs se preservan tal cual
 * (son clonables nativamente); los arrays y objetos anidados se
 * recorren y se les aplica `toRaw` recursivamente.
 *
 * Necesario porque los stores de Pinia devuelven Proxies reactivos, y
 * IndexedDB lanza DataCloneError al intentar clonarlos.
 */
function toPlain<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') return obj
  // Blobs, Files, Date, etc. son clonables nativamente; no tocar.
  if (obj instanceof Blob || obj instanceof Date || obj instanceof File) return obj
  if (Array.isArray(obj)) {
    return obj.map((item) => toRawDeep(item)) as unknown as T
  }
  const raw = isProxy(obj) ? toRaw(obj) : obj
  const out: Record<string, unknown> = {}
  for (const key of Object.keys(raw as Record<string, unknown>)) {
    out[key] = toRawDeep((raw as Record<string, unknown>)[key])
  }
  return out as unknown as T
}

function toRawDeep<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') return obj
  if (obj instanceof Blob || obj instanceof Date || obj instanceof File) return obj
  if (Array.isArray(obj)) return obj.map((item) => toRawDeep(item)) as unknown as T
  return toPlain(obj)
}

// ---------- Books ----------

export async function getAllBooks(): Promise<Book[]> {
  // Ordenados por fecha de adición descendente (más recientes primero).
  return db.books.orderBy('dateAdded').reverse().toArray()
}

export async function getBook(id: string): Promise<Book | undefined> {
  return db.books.get(id)
}

export async function saveBook(book: Book): Promise<void> {
  try {
    await db.books.put(toPlain(book))
  } catch (e) {
    // Los EPUBs se guardan enteros como Blob; llenar la cuota es el
    // fallo más probable al importar varios libros grandes. Lo tipamos
    // para que la UI pueda mostrar un mensaje accionable.
    if (isQuotaError(e)) throw new StorageQuotaError()
    throw e
  }
}

/**
 * Devuelve el binario EPUB de un libro (v18: vive en su propia tabla).
 * `undefined` si el libro no existe o no tiene archivo (caso corrupto).
 */
export async function getBookFile(id: string): Promise<Blob | undefined> {
  const row = await db.bookFiles.get(id)
  return row?.blob
}

/**
 * Devuelve la portada de un libro (v18: vive en su propia tabla).
 * `null` si no tiene portada (muestra el fallback editorial).
 */
export async function getBookCover(id: string): Promise<Blob | null> {
  const row = await db.bookCovers.get(id)
  return row?.blob ?? null
}

/** Persiste el binario EPUB de un libro en su tabla propia (v18). */
export async function saveBookFile(id: string, blob: Blob): Promise<void> {
  try {
    await db.bookFiles.put({ id, blob })
  } catch (e) {
    if (isQuotaError(e)) throw new StorageQuotaError()
    throw e
  }
}

/** Persiste la portada de un libro en su tabla propia (v18). */
export async function saveBookCover(id: string, blob: Blob): Promise<void> {
  try {
    await db.bookCovers.put({ id, blob })
  } catch (e) {
    if (isQuotaError(e)) throw new StorageQuotaError()
    throw e
  }
}

export async function deleteBook(id: string): Promise<void> {
  // Borrar en cascada: anotaciones, resúmenes, traducciones, índice,
  // embeddings, tarjetas de repaso, y los blobs separados (v18).
  // Pasamos las tablas como array porque el overload variádico de Dexie
  // tipa hasta un número limitado; el formulario con array no tiene límite.
  await db.transaction(
    'rw',
    [db.books, db.highlights, db.summaries, db.translations, db.bookIndex, db.embeddings, db.reviewCards, db.bookFiles, db.bookCovers, db.audioCache],
    async () => {
      await db.highlights.where('bookId').equals(id).delete()
      await db.summaries.where('bookId').equals(id).delete()
      await db.translations.where('bookId').equals(id).delete()
      await db.bookIndex.where('bookId').equals(id).delete()
      await db.embeddings.where('bookId').equals(id).delete()
      await db.reviewCards.where('bookId').equals(id).delete()
      await db.bookFiles.delete(id)
      await db.bookCovers.delete(id)
      await db.audioCache.where('bookId').equals(id).delete()
      await db.books.delete(id)
    },
  )
}

/**
 * Borra varios libros en una SOLA transacción. Mucho más eficiente que
 * llamar a `deleteBook` N veces (cada una abriría su propia transacción
 * sobre 9 tablas). Usa `anyOf(ids)` para el borrado en cascada de las
 * tablas relacionadas y `bulkDelete` para las tablas de blobs.
 */
export async function deleteBooks(ids: string[]): Promise<void> {
  if (ids.length === 0) return
  await db.transaction(
    'rw',
    [db.books, db.highlights, db.summaries, db.translations, db.bookIndex, db.embeddings, db.reviewCards, db.bookFiles, db.bookCovers, db.audioCache],
    async () => {
      await db.highlights.where('bookId').anyOf(ids).delete()
      await db.summaries.where('bookId').anyOf(ids).delete()
      await db.translations.where('bookId').anyOf(ids).delete()
      await db.bookIndex.where('bookId').anyOf(ids).delete()
      await db.embeddings.where('bookId').anyOf(ids).delete()
      await db.reviewCards.where('bookId').anyOf(ids).delete()
      await db.bookFiles.bulkDelete(ids)
      await db.bookCovers.bulkDelete(ids)
      await db.audioCache.where('bookId').anyOf(ids).delete()
      await db.books.bulkDelete(ids)
    },
  )
}

// ---------- Highlights ----------

export async function getHighlightsForBook(bookId: string): Promise<Highlight[]> {
  return db.highlights.where('[bookId+createdAt]').between([bookId, ''], [bookId, '\uffff']).toArray()
}

export async function saveHighlight(highlight: Highlight): Promise<void> {
  await db.highlights.put(toPlain(highlight))
}

export async function deleteHighlight(id: string): Promise<void> {
  await db.highlights.delete(id)
}

// ---------- Summaries ----------

export async function getSummariesForBook(bookId: string): Promise<Summary[]> {
  return db.summaries.where('[bookId+createdAt]').between([bookId, ''], [bookId, '\uffff']).toArray()
}

export async function saveSummary(summary: Summary): Promise<void> {
  await db.summaries.put(toPlain(summary))
}

export async function deleteSummary(id: string): Promise<void> {
  await db.summaries.delete(id)
}

// ---------- Translations ----------

export async function getTranslationsForBook(bookId: string): Promise<Translation[]> {
  return db.translations
    .where('[bookId+createdAt]')
    .between([bookId, ''], [bookId, '\uffff'])
    .toArray()
}

export async function saveTranslation(translation: Translation): Promise<void> {
  await db.translations.put(toPlain(translation))
}

export async function deleteTranslation(id: string): Promise<void> {
  await db.translations.delete(id)
}

// ---------- Bookmarks ----------

export async function getBookmarksForBook(bookId: string): Promise<Bookmark[]> {
  return db.bookmarks.where('[bookId+createdAt]').between([bookId, ''], [bookId, '\uffff']).toArray()
}

export async function saveBookmark(bookmark: Bookmark): Promise<void> {
  try {
    await db.bookmarks.put(toPlain(bookmark))
  } catch (e) {
    if (isQuotaError(e)) throw new StorageQuotaError()
    throw e
  }
}

export async function deleteBookmark(id: string): Promise<void> {
  await db.bookmarks.delete(id)
}

// ---------- Bulk (para backup de biblioteca y estadísticas globales) ----------

/** Todos los subrayados de todas partes (para backup y Wrapped). */
export async function getAllHighlights(): Promise<Highlight[]> {
  return db.highlights.toArray()
}

/** Todos los resúmenes (para backup). */
export async function getAllSummaries(): Promise<Summary[]> {
  return db.summaries.toArray()
}

/** Todas las traducciones (para backup). */
export async function getAllTranslations(): Promise<Translation[]> {
  return db.translations.toArray()
}

/** Todos los marcadores (para backup). */
export async function getAllBookmarks(): Promise<Bookmark[]> {
  return db.bookmarks.toArray()
}

// ---------- Reconciliación tras sync remoto ----------
//
// Cuando un cambio llega por el CRDT (Yjs) y se aplica al array local en
// memoria, IndexedDB no se entera (los stores solo persisten en acciones
// explícitas del usuario). Estos helpers reconcilian el estado canónico
// (el del CRDT, ya fusionado) contra lo almacenado localmente: hacen
// upsert de los presentes y borran los ausentes, por libro.

/**
 * Reconcilia una colección sincronizada contra IndexedDB.
 *
 * @param table       Tabla Dexie destino.
 * @param bookId      Libro al que pertenecen los items (para saber qué borrar).
 * @param syncedItems Estado canónico venido del CRDT.
 */
async function reconcileSynced<T extends { id: string; bookId: string }>(
  table: Table<T, string>,
  bookId: string,
  syncedItems: T[],
): Promise<void> {
  // Upsert de los presentes.
  for (const item of syncedItems) {
    await table.put(toPlain(item))
  }
  // Borrar los ausentes: todo lo del libro que no esté en el set sincronizado.
  const syncedIds = new Set(syncedItems.map((it) => it.id))
  const localRows = await table.where('bookId').equals(bookId).toArray()
  const toDelete = localRows.filter((row) => !syncedIds.has(row.id)).map((row) => row.id)
  if (toDelete.length > 0) await table.bulkDelete(toDelete)
}

export async function reconcileHighlights(bookId: string, items: Highlight[]): Promise<void> {
  await reconcileSynced(db.highlights, bookId, items)
}

/**
 * Reconciliar comentarios de highlights tras un cambio CRDT (F2.1).
// A diferencia de `reconcileHighlights`, los comentarios no tienen bookId;
// se borran los locales cuyo id no esté en el set sincronizado (los
// comentarios sólo viven en rooms compartidos, así que el set completo es
// siempre el del libro abierto).
 */
export async function reconcileHighlightComments(items: HighlightComment[]): Promise<void> {
  const syncedIds = new Set(items.map((it) => it.id))
  // Upsert presentes.
  for (const item of items) {
    await db.highlightComments.put(toPlain(item))
  }
  // Borrar locales ausentes.
  const all = await db.highlightComments.toArray()
  const toDelete = all.filter((row) => !syncedIds.has(row.id)).map((row) => row.id)
  if (toDelete.length > 0) await db.highlightComments.bulkDelete(toDelete)
}

/** Carga los comentarios de un conjunto de highlights (los del libro abierto). */
export async function getCommentsForHighlights(highlightIds: string[]): Promise<HighlightComment[]> {
  if (highlightIds.length === 0) return []
  const result: HighlightComment[] = []
  for (const hid of highlightIds) {
    const rows = await db.highlightComments
      .where('[highlightId+createdAt]')
      .between([hid, ''], [hid, '\uffff'])
      .toArray()
    result.push(...rows)
  }
  return result
}

/** Inserta/actualiza un comentario individual. */
export async function saveHighlightComment(comment: HighlightComment): Promise<void> {
  await db.highlightComments.put(toPlain(comment))
}

/** Borra un comentario por id. */
export async function deleteHighlightComment(id: string): Promise<void> {
  await db.highlightComments.delete(id)
}

export async function reconcileSummaries(bookId: string, items: Summary[]): Promise<void> {
  await reconcileSynced(db.summaries, bookId, items)
}

export async function reconcileTranslations(bookId: string, items: Translation[]): Promise<void> {
  await reconcileSynced(db.translations, bookId, items)
}

export async function reconcileBookmarks(bookId: string, items: Bookmark[]): Promise<void> {
  await reconcileSynced(db.bookmarks, bookId, items)
}

// ---------- Shelves ----------

/**
 * Devuelve todas las estanterías del usuario. Las estanterías son
 * globales (no scoped por libro), así que no hay filtro por bookId.
 */
export async function getAllShelves(): Promise<Shelf[]> {
  return db.shelves.orderBy('createdAt').toArray()
}

export async function saveShelf(shelf: Shelf): Promise<void> {
  await db.shelves.put(toPlain(shelf))
}

export async function deleteShelf(id: string): Promise<void> {
  await db.shelves.delete(id)
}

/**
 * Reconcilia TODAS las estanterías contra el estado canónico del CRDT.
 *
 * A diferencia de `reconcileSynced` (que opera por libro), aquí no hay
 * `bookId` que filtrar: el doc CRDT de estanterías es global por
 * usuario, así que el set sincronizado representa el estado completo.
 * Hacemos upsert de los presentes y borramos los ausentes a nivel tabla.
 */
export async function reconcileShelves(items: Shelf[]): Promise<void> {
  for (const item of items) {
    await db.shelves.put(toPlain(item))
  }
  const syncedIds = new Set(items.map((it) => it.id))
  const localRows = await db.shelves.toArray()
  const toDelete = localRows.filter((row) => !syncedIds.has(row.id)).map((row) => row.id)
  if (toDelete.length > 0) await db.shelves.bulkDelete(toDelete)
}

// ---------- Favorites ----------

/** Una fila de favoritos: id = bookId, addedAt = cuándo se marcó. */
export interface FavoriteRow {
  id: string
  addedAt: string
}

/** Devuelve todos los favoritos (caché local del CRDT favorites:${userId}). */
export async function getAllFavorites(): Promise<FavoriteRow[]> {
  return db.favorites.toArray()
}

/** Persiste/actualiza un favorito local (caché del CRDT). */
export async function putFavorite(row: FavoriteRow): Promise<void> {
  await db.favorites.put(row)
}

/** Borra un favorito local por bookId. */
export async function deleteFavorite(bookId: string): Promise<void> {
  await db.favorites.delete(bookId)
}

/**
 * Reconcilia TODOS los favoritos contra el estado canónico del CRDT.
 * Igual que `reconcileShelves`: upsert de los presentes, borrado de los
 * ausentes. El doc CRDT de favoritos es global por usuario.
 */
export async function reconcileFavorites(items: FavoriteRow[]): Promise<void> {
  for (const item of items) {
    await db.favorites.put(toPlain(item))
  }
  const syncedIds = new Set(items.map((it) => it.id))
  const localRows = await db.favorites.toArray()
  const toDelete = localRows.filter((row) => !syncedIds.has(row.id)).map((row) => row.id)
  if (toDelete.length > 0) await db.favorites.bulkDelete(toDelete)
}

// ---------- Reading sessions ----------

export async function saveSession(session: ReadingSession): Promise<void> {
  await db.sessions.put(toPlain(session))
}

export async function getSessionsForBook(bookId: string): Promise<ReadingSession[]> {
  return db.sessions.where('bookId').equals(bookId).toArray()
}

export async function getAllSessions(): Promise<ReadingSession[]> {
  return db.sessions.toArray()
}

// ---------- Settings ----------

export async function loadSettings(): Promise<Settings> {
  try {
    const row = await db.settings.get(SETTINGS_KEY)
    if (!row) return structuredClone(DEFAULT_SETTINGS)
    // Validación mínima: el valor debe ser un objeto plano. Una fila
    // corrupta (escritura interrumpida, versión antigua incompatible)
    // no debe tirar la app al arrancar.
    if (!row.value || typeof row.value !== 'object' || Array.isArray(row.value)) {
      console.warn('[settings] fila corrupta o inválida; usando defaults.')
      // Re-grabar defaults limpios para no volver a leer el valor malo.
      await saveSettings(structuredClone(DEFAULT_SETTINGS)).catch(() => {})
      return structuredClone(DEFAULT_SETTINGS)
    }
    // Merge profundo con los defaults para tolerar añadir campos nuevos
    // en futuras versiones sin romper settings antiguas.
    return mergeSettings(structuredClone(DEFAULT_SETTINGS), row.value as Partial<Settings>)
  } catch (e) {
    // Cualquier otro fallo (Dexie, structuredClone sobre datos raros, etc.):
    // la app debe arrancar siempre con defaults.
    console.warn('[settings] error al cargar; usando defaults:', e)
    return structuredClone(DEFAULT_SETTINGS)
  }
}

export async function saveSettings(settings: Settings): Promise<void> {
  await db.settings.put({ key: SETTINGS_KEY, value: toPlain(settings) })
}

/** Merge recursivo de settings parciales sobre la base (defaults). */
function mergeSettings(base: Settings, partial: Partial<Settings>): Settings {
  const out = { ...base, ...partial }
  out.typography = { ...base.typography, ...(partial.typography ?? {}) }
  out.ai = {
    ...base.ai,
    ...(partial.ai ?? {}),
    providers: {
      ...base.ai.providers,
      // Mezcla proveedor a proveedor para no perder los que falten.
      ...(partial.ai?.providers ?? {}),
    },
  }
  out.sync = { ...base.sync, ...(partial.sync ?? {}) }
  out.typography = { ...base.typography, ...(partial.typography ?? {}) }
  out.libraryFilters = { ...base.libraryFilters, ...(partial.libraryFilters ?? {}) }
  out.highlightColors = { ...base.highlightColors, ...(partial.highlightColors ?? {}) }
  out.accessibility = { ...base.accessibility, ...(partial.accessibility ?? {}) }
  out.pomodoro = { ...base.pomodoro, ...(partial.pomodoro ?? {}) }
  out.audio = { ...base.audio, ...(partial.audio ?? {}) }
  // Los campos de Supabase (URL + clave) pueden venir vacíos en una
  // fila persistida de un build anterior (cuando aún no leían de env).
  // Un vacío NO debe pisar el default (que ahora se alimenta de
  // VITE_SUPABASE_*), o el login fallaría con "configura Supabase".
  // Solo aplicamos el valor persistido si no está vacío.
  if (!out.sync.supabaseUrl) out.sync.supabaseUrl = base.sync.supabaseUrl
  if (!out.sync.supabaseAnonKey) out.sync.supabaseAnonKey = base.sync.supabaseAnonKey
  return out
}

// ---------- Book index (búsqueda global) ----------

/** Todos los chunks indexados (para reconstruir el índice FlexSearch al arrancar). */
export async function getAllIndexChunks(): Promise<BookIndexChunk[]> {
  return db.bookIndex.toArray()
}

/** Chunks de un libro concreto (para saber si ya está indexado). */
export async function getIndexChunksForBook(bookId: string): Promise<BookIndexChunk[]> {
  return db.bookIndex.where('bookId').equals(bookId).toArray()
}

export async function bulkPutIndexChunks(chunks: BookIndexChunk[]): Promise<void> {
  await db.bookIndex.bulkPut(chunks.map(toPlain))
}

export async function deleteIndexChunksForBook(bookId: string): Promise<void> {
  await db.bookIndex.where('bookId').equals(bookId).delete()
}

/** Borra los chunks de highlight de un libro (al reindexar anotaciones). */
export async function deleteHighlightChunksForBook(bookId: string): Promise<void> {
  await db.bookIndex.where('[bookId+kind]').equals([bookId, 'highlight']).delete()
}

export async function deleteIndexChunk(id: string): Promise<void> {
  await db.bookIndex.delete(id)
}

// ---------- Embeddings (segundo cerebro, Área C) ----------

export async function getAllEmbeddings(): Promise<EmbeddingRow[]> {
  return db.embeddings.toArray()
}

export async function getEmbeddingsForBook(bookId: string): Promise<EmbeddingRow[]> {
  return db.embeddings.where('bookId').equals(bookId).toArray()
}

export async function getEmbedding(id: string): Promise<EmbeddingRow | undefined> {
  return db.embeddings.get(id)
}

export async function putEmbedding(row: EmbeddingRow): Promise<void> {
  await db.embeddings.put(toPlain(row))
}

export async function bulkPutEmbeddings(rows: EmbeddingRow[]): Promise<void> {
  await db.embeddings.bulkPut(rows.map(toPlain))
}

export async function deleteEmbeddingsForBook(bookId: string): Promise<void> {
  await db.embeddings.where('bookId').equals(bookId).delete()
}

export async function deleteEmbedding(id: string): Promise<void> {
  await db.embeddings.delete(id)
}

// ---------- Chat threads (RAG, Área D) ----------

import type { ChatThread } from '@/types/chat'

export async function getThreadsForBook(bookId: string): Promise<ChatThread[]> {
  return db.chatThreads.where('bookId').equals(bookId).reverse().sortBy('updatedAt')
}

export async function getLibraryThreads(): Promise<ChatThread[]> {
  return db.chatThreads.where('scope').equals('library').reverse().sortBy('updatedAt')
}

export async function getChatThread(id: string): Promise<ChatThread | undefined> {
  return db.chatThreads.get(id)
}

export async function saveChatThread(thread: ChatThread): Promise<void> {
  await db.chatThreads.put(toPlain(thread))
}

export async function deleteChatThread(id: string): Promise<void> {
  await db.chatThreads.delete(id)
}

export async function deleteChatThreadsForBook(bookId: string): Promise<void> {
  await db.chatThreads.where('bookId').equals(bookId).delete()
}

// ---------- Review cards (SRS, Área E) ----------

import type { ReviewCard } from '@/types/domain'

export async function getAllReviewCards(): Promise<ReviewCard[]> {
  return db.reviewCards.toArray()
}

export async function getDueReviewCards(todayKey: string): Promise<ReviewCard[]> {
  return db.reviewCards.where('dueDate').belowOrEqual(todayKey).toArray()
}

export async function getReviewCardByHighlight(highlightId: string): Promise<ReviewCard | undefined> {
  return db.reviewCards.where('highlightId').equals(highlightId).first()
}

export async function saveReviewCard(card: ReviewCard): Promise<void> {
  await db.reviewCards.put(toPlain(card))
}

export async function deleteReviewCard(id: string): Promise<void> {
  await db.reviewCards.delete(id)
}

export async function deleteReviewCardByHighlight(highlightId: string): Promise<void> {
  await db.reviewCards.where('highlightId').equals(highlightId).delete()
}

export async function deleteReviewCardsForBook(bookId: string): Promise<void> {
  await db.reviewCards.where('bookId').equals(bookId).delete()
}

/**
 * Reconcilia TODAS las tarjetas de repaso contra el estado canónico del CRDT
 * (global por usuario). Upsert de presentes + borrado de ausentes a nivel tabla.
 */
export async function reconcileReviewCards(items: ReviewCard[]): Promise<void> {
  for (const item of items) {
    await db.reviewCards.put(toPlain(item))
  }
  const syncedIds = new Set(items.map((it) => it.id))
  const localRows = await db.reviewCards.toArray()
  const toDelete = localRows.filter((row) => !syncedIds.has(row.id)).map((row) => row.id)
  if (toDelete.length > 0) await db.reviewCards.bulkDelete(toDelete)
}

// ---------- Caché de audio (modo audiolibro) ----------

/**
 * Bytes "reales" de una fila del caché: usa `bytes` si es un número
 * positivo válido, y si no, cae a `blob.size`.
 *
 * Defensa contra filas con `bytes` corrupto (p. ej. una escritura
 * interrumpida que deja `bytes: 0` mientras el blob real pesa más). Sin
 * esto, `audioCacheBytes()` y el desalojo LRU heredan el valor erróneo:
 * el total nunca refleja el espacio real y borrar la fila corrupta no lo
 * corrige, así que el desalojo puede comerse filas sanas de más (o dejar
 * de desalojar cuando debía). Se usa tanto para sumar el total como para
 * descontar dentro del bucle de desalojo, para que ambos coincidan.
 *
 * Exportada (no solo interna) para poder testearla de forma directa y
 * determinista: `fake-indexeddb` + jsdom no reconstruyen fielmente un
 * `Blob` tras un roundtrip de IndexedDB en este entorno de test (el
 * objeto recuperado pierde `.size`), así que el camino "corrupto en DB →
 * cae a blob.size" no es verificable end-to-end aquí. La lógica en sí
 * (esta función) sí lo es, sin depender de ese roundtrip.
 */
export function effectiveAudioBytes(row: AudioChunkRow): number {
  return typeof row.bytes === 'number' && row.bytes > 0 ? row.bytes : (row.blob?.size ?? 0)
}

/** Devuelve un fragmento de audio cacheado, o undefined si no está. */
export async function getAudioChunk(id: string): Promise<AudioChunkRow | undefined> {
  return await db.audioCache.get(id)
}

/**
 * Guarda (o reemplaza) un fragmento de audio en el caché.
 *
 * `bytes` se deriva de `blob.size` en vez de confiar en lo que pase el
 * llamador: leer `.size` de un Blob es metadato (no carga el contenido),
 * así que es gratis, y evita que un `bytes` manual desincronizado del
 * blob real corrompa la cuenta que usa el desalojo LRU.
 */
export async function putAudioChunk(row: AudioChunkRow): Promise<void> {
  await db.audioCache.put({ ...row, bytes: row.blob.size })
}

/** Marca un fragmento como usado ahora (mueve su posición en el LRU). */
export async function touchAudioChunk(id: string): Promise<void> {
  await db.audioCache.update(id, { lastUsedAt: new Date().toISOString() })
}

/** Bytes totales ocupados por el caché de audio. */
export async function audioCacheBytes(): Promise<number> {
  let total = 0
  await db.audioCache.each((row) => { total += effectiveAudioBytes(row) })
  return total
}

/**
 * Desaloja fragmentos por LRU hasta que el caché quepa en `limitBytes`.
 * Devuelve los bytes liberados.
 *
 * `keepId` protege el fragmento que está sonando: borrarlo cortaría la
 * reproducción en curso. Si al saltárselo ya no queda nada más que borrar,
 * la función sale sin cumplir el tope (mejor pasarse que cortar el audio).
 */
export async function evictAudioCache(
  limitBytes: number,
  keepId: string | null,
): Promise<number> {
  let total = await audioCacheBytes()
  if (total <= limitBytes) return 0

  // Más antiguos primero: el índice `lastUsedAt` ya los da ordenados.
  const candidates = await db.audioCache.orderBy('lastUsedAt').toArray()
  let freed = 0
  for (const row of candidates) {
    if (total <= limitBytes) break
    if (row.id === keepId) continue
    await db.audioCache.delete(row.id)
    // Mismo cálculo que el total inicial (`effectiveAudioBytes`): si se
    // usara `row.bytes` a pelo aquí, una fila corrupta restaría de menos
    // y el bucle seguiría comiéndose filas sanas para intentar cuadrar
    // un total que nunca baja lo que debería.
    const bytes = effectiveAudioBytes(row)
    total -= bytes
    freed += bytes
  }
  return freed
}

/** Vacía el caché de audio completo (acción de Ajustes). */
export async function clearAudioCache(): Promise<void> {
  await db.audioCache.clear()
}

/** Borra el audio cacheado de un libro (cascada al eliminarlo). */
export async function deleteAudioChunksForBook(bookId: string): Promise<void> {
  await db.audioCache.where('bookId').equals(bookId).delete()
}
